import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Shield, RefreshCw, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Flex, Badge, Spinner, LoadingContainer, Input, Select, Label, FormGroup, Button
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

function formatTs(ts, lang) {
  if (!ts) return '—';
  return new Intl.DateTimeFormat(lang, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).format(new Date(ts));
}

function extraFields(item) {
  const skip = new Set(['PK','SK','GSI1PK','GSI1SK','action','tenantId','actorId','actorRole','actorEmail','timestamp','ttl']);
  return Object.entries(item)
    .filter(([k]) => !skip.has(k))
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(' · ');
}

export default function AuditLogPage() {
  const { t, i18n } = useTranslation('common');
  const { tenantId } = useParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [nextKey, setNextKey]   = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters]   = useState({ action: '', from: '', to: '' });

  const ACTION_KEYS = [
    'tenant.create','tenant.update','tenant.self_register',
    'connector.create','connector.update','connector.delete',
    'agent.install','agent.update','agent.uninstall',
    'member.create','member.update','member.delete',
    'knowledge.upload','knowledge.delete',
    'billing.upgrade','billing.downgrade_scheduled',
    'session.transfer','session.resolve',
  ];

  const breadcrumbItems = [
    isSallusAdmin
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : { label: t('home'), path: `/dashboard/${tenantId}` },
    { label: t('audit.title'), icon: '🛡️' }
  ];

  const load = useCallback(async (reset = true) => {
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const qs = new URLSearchParams({ limit: 50 });
      if (filters.action) qs.set('action', filters.action);
      if (filters.from)   qs.set('from', filters.from + 'T00:00:00.000Z');
      if (filters.to)     qs.set('to',   filters.to   + 'T23:59:59.999Z');
      if (!reset && nextKey) qs.set('nextKey', nextKey);

      const res = await api.get(`/audit/${tenantId}?${qs}`);
      const data = res.data;
      setItems(prev => reset ? data.items : [...prev, ...data.items]);
      setNextKey(data.nextKey || null);
    } catch (e) { console.error(e); }
    finally { reset ? setLoading(false) : setLoadingMore(false); }
  }, [tenantId, filters, nextKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(true); }, [tenantId, filters]); // eslint-disable-line

  return (
    <>
      <AppHeader title={t('audit.title')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>🛡️ {t('audit.titleFull')}</PageTitle>
            <PageSubtitle>{t('audit.subtitle')}</PageSubtitle>
          </div>
          <Button variant="secondary" size="sm" onClick={() => load(true)}>
            <RefreshCw size={14} /> {t('audit.refresh')}
          </Button>
        </PageHeader>

        <Card style={{ marginBottom: theme.spacing[4] }}>
          <Flex gap={theme.spacing[3]} style={{ flexWrap: 'wrap' }}>
            <FormGroup style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
              <Label>{t('audit.filterAction')}</Label>
              <Select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
                <option value="">{t('audit.filterAll')}</option>
                {ACTION_KEYS.map(k => (
                  <option key={k} value={k}>{t(`audit.actions.${k}`, { defaultValue: k })}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup style={{ flex: 1, minWidth: 150, marginBottom: 0 }}>
              <Label>{t('audit.filterFrom')}</Label>
              <Input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
            </FormGroup>
            <FormGroup style={{ flex: 1, minWidth: 150, marginBottom: 0 }}>
              <Label>{t('audit.filterTo')}</Label>
              <Input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
            </FormGroup>
            {(filters.action || filters.from || filters.to) && (
              <FormGroup style={{ marginBottom: 0, alignSelf: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => setFilters({ action: '', from: '', to: '' })}>
                  {t('clearFilters')}
                </Button>
              </FormGroup>
            )}
          </Flex>
        </Card>

        {loading ? (
          <LoadingContainer><Spinner /><span>{t('loading')}</span></LoadingContainer>
        ) : items.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: theme.spacing[8], color: theme.colors.textMuted }}>
            <Shield size={40} strokeWidth={1} style={{ marginBottom: theme.spacing[3] }} />
            <div>{t('audit.empty')}</div>
          </Card>
        ) : (
          <>
            <AuditTable>
              <thead>
                <tr>
                  <Th>{t('audit.colDateTime')}</Th>
                  <Th>{t('audit.colAction')}</Th>
                  <Th>{t('audit.colActor')}</Th>
                  <Th>{t('audit.colRole')}</Th>
                  <Th>{t('audit.colDetails')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  const actionLabel = t(`audit.actions.${item.action}`, { defaultValue: item.action });
                  const roleLabel   = t(`audit.roles.${item.actorRole}`, { defaultValue: item.actorRole });
                  const extra = extraFields(item);
                  return (
                    <tr key={i}>
                      <Td style={{ whiteSpace: 'nowrap', color: theme.colors.textMuted, fontSize: theme.typography.xs }}>
                        {formatTs(item.timestamp, i18n.language)}
                      </Td>
                      <Td><Badge variant="neutral">{actionLabel}</Badge></Td>
                      <Td style={{ fontSize: theme.typography.xs }}>
                        {item.actorEmail || item.actorId || '—'}
                      </Td>
                      <Td style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>
                        {roleLabel}
                      </Td>
                      <Td style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted, maxWidth: 300 }}>
                        {extra || '—'}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </AuditTable>

            {nextKey && (
              <Flex style={{ justifyContent: 'center', marginTop: theme.spacing[4] }}>
                <Button variant="secondary" size="sm" onClick={() => load(false)} disabled={loadingMore}>
                  {loadingMore ? <Spinner size={14} /> : <ChevronDown size={14} />}
                  {' '}{loadingMore ? t('loading') : t('loadMore')}
                </Button>
              </Flex>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
}

const AuditTable = styled.table`width: 100%; border-collapse: collapse; background: ${theme.colors.white}; border-radius: ${theme.radii.lg}; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08);`;
const Th = styled.th`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; text-align: left; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 2px solid ${theme.colors.border}; background: ${theme.colors.gray50};`;
const Td = styled.td`font-size: ${theme.typography.sm}; color: ${theme.colors.textPrimary}; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.gray100}; vertical-align: middle;`;
